import { Layer, MapRef, PointLike, Source } from "react-map-gl";
import useProjectVariations from "src/api/hooks/useProjectVariations";
import { useApplyFilters } from "src/utils/applyFilter";
import { mapSettings } from "../mapSettings";
import { RefObject, useCallback, useMemo, useState, useEffect } from "react";
import { useProjectVariationById } from "src/api/hooks/useProjectVariationById";
import { useParams } from "react-router-dom";
import {
  InspectionResponse,
  SasFileResult,
} from "src/types/InspectionUpdateRequest";
import { useAppContext } from "src/components/Context";
import { featureColor } from "../featureColor";
import { GeoJSONFiles } from "./GeoJSONFiles";
import { useMapFiles } from "./GeoJSONFiles";


// Calculate bounding box for a polygon geometry
function getPolygonBounds(geometry: any): [PointLike, PointLike] | null {
  if (!geometry || !geometry.coordinates) return null;

  let allCoords: number[][] = [];

  if (geometry.type === "Polygon") {
    // For Polygon, coordinates is an array of rings
    allCoords = geometry.coordinates[0] || [];
  } else if (geometry.type === "MultiPolygon") {
    // For MultiPolygon, coordinates is an array of polygons
    allCoords = geometry.coordinates.flat().flat();
  }

  if (allCoords.length === 0) return null;

  let minLng = allCoords[0][0];
  let maxLng = allCoords[0][0];
  let minLat = allCoords[0][1];
  let maxLat = allCoords[0][1];

  allCoords.forEach((coord: number[]) => {
    minLng = Math.min(minLng, coord[0]);
    maxLng = Math.max(maxLng, coord[0]);
    minLat = Math.min(minLat, coord[1]);
    maxLat = Math.max(maxLat, coord[1]);
  });

  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ];
}

// Check if map bounds are within polygon bounds
function isMapBoundsWithinPolygon(
  mapBounds: [PointLike, PointLike],
  polygonBounds: [PointLike, PointLike]
): boolean {
  const [mapSW, mapNE] = mapBounds;
  const [polySW, polyNE] = polygonBounds;

  return (
    mapSW[0] >= polySW[0] && // map west >= polygon west
    mapNE[0] <= polyNE[0] && // map east <= polygon east
    mapSW[1] >= polySW[1] && // map south >= polygon south
    mapNE[1] <= polyNE[1] // map north <= polygon north
  );
}



// Check if a point is within a polygon geometry


// Calculate the center point of a polygon geometry
function getPolygonCenter(geometry: any): [number, number] | null {
  if (!geometry || !geometry.coordinates) return null;

  let allCoords: number[][] = [];

  if (geometry.type === "Polygon") {
    allCoords = geometry.coordinates[0] || [];
  } else if (geometry.type === "MultiPolygon") {
    allCoords = geometry.coordinates.flat().flat();
  }

  if (allCoords.length === 0) return null;

  const sumLng = allCoords.reduce((sum, coord) => sum + coord[0], 0);
  const sumLat = allCoords.reduce((sum, coord) => sum + coord[1], 0);

  return [sumLng / allCoords.length, sumLat / allCoords.length];
}

function createFeatures(
  d: InspectionResponse | SasFileResult,
  displayeditems: InspectionResponse[],
): any {
  const mainFeature = createFeature(d, displayeditems);
  let childFeatures: any[] = [];
  if ((d as InspectionResponse).children) {
    childFeatures = (d as InspectionResponse).children?.map((c) =>
      createFeatures(c, displayeditems)
    );
  }
  if (d.files && (d as InspectionResponse).userId) {
    childFeatures = [
      ...childFeatures,
      ...(d as InspectionResponse).files
        .map((f) => createFeature(f, displayeditems))
        .flat(),
    ];
  }
  return [mainFeature, ...childFeatures].flat();
}

export function createFeature(
  d: InspectionResponse | SasFileResult,
  displayeditems: InspectionResponse[]
): any {
  let featureType = (d as InspectionResponse).objectType || "Image";
  if ((d as SasFileResult).imageResponse?.urlOriginal) {
    const item_for_file = displayeditems?.find(
      (item) => item.id === (d as SasFileResult).deviationId
    );
    if (item_for_file) {
      featureType = `${item_for_file.objectType}-Image`;
    } else {
      featureType = "Image";
    }
  }
  if (d.position?.geometry) {
    try {
      const parsedGeometry = JSON.parse(d.position.geometry);
      if (parsedGeometry.type === "Point") {
        
      }
      return {
        id: `${featureType}-${d.id}`,
        type: "Feature",
        source: mapSettings.uniqueSourcePrefix,
        geometry:
          parsedGeometry.type === "MultiPolygon"
            ? { ...parsedGeometry, coordinates: parsedGeometry.coordinates }
            : parsedGeometry,
        properties: {
          ...d,
          markerColor: featureColor[featureType],
          featureType: featureType,
          geometryType: parsedGeometry.type,
        },
      };
    } catch (error) {
      console.error(
        "Error parsing geometry:",
        error,
        "Geometry string:",
        d.position.geometry
      );
    }
  } else {
    const lat = (d as InspectionResponse).latitude ?? d.position?.latitude;
    const lon = (d as InspectionResponse).longitude ?? d.position?.longitude;
    return [
      {
        id: `${featureType}-${d.id}`,
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: [lon, lat], // GeoJSON uses [longitude, latitude]
        },
        properties: {
          ...d,
          featureType: featureType,
          markerColor: featureColor[featureType],
          geometryType: "Point",
        },
        source: mapSettings.uniqueSourcePrefix,
      },
    ];
  }
}
// Hook to track map bounds
export function useMapBounds() {
  const { mapRef } = useAppContext();
  const [mapBounds, setMapBounds] = useState<[PointLike, PointLike] | null>(
    null
  );

  useEffect(() => {
    if (!mapRef?.current) return;

    const updateBounds = () => {
      const bounds = mapRef.current?.getBounds();
      if (bounds) {
        setMapBounds([
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ]);
      }
    };

    // Initial bounds
    updateBounds();

    // Listen for map move events
    const map = mapRef.current;
    map.on("moveend", updateBounds);
    map.on("zoomend", updateBounds);

    return () => {
      map.off("moveend", updateBounds);
      map.off("zoomend", updateBounds);
    };
  }, [mapRef]);

  return mapBounds;
}

export function useFeatures() {
  const { data: displayedItems } = useProjectVariations();
  const { deviationId, inspectionId, companyId } = useParams();
  const applyFilters = useApplyFilters(
    [deviationId, inspectionId, companyId].filter(Boolean) as string[]
  );
  const { data: currentItem } = useProjectVariationById(
    deviationId ?? inspectionId
  );
  const mapBounds = useMapBounds();

  return useMemo(() => {
    if (!displayedItems || !displayedItems  .length)
      return { points: [], polygons: [] };
    let filteredItems = displayedItems;
    if ((deviationId || inspectionId) && currentItem) {
      filteredItems = [currentItem];
    }
    if (!deviationId && !inspectionId) {
      filteredItems = applyFilters(displayedItems);
    }
    let featuresToReturn = filteredItems
      .map((item) => createFeatures(item, displayedItems))
      .flat();

    const allPolygons = featuresToReturn.filter(
      (f) =>
        f?.properties && (f.properties?.geometryType === "MultiPolygon" ||
        f.properties?.geometryType === "Polygon")
    );

    // Filter polygons based on visibility - hide if map bounds are within polygon bounds
    const visiblePolygons = allPolygons.filter((polygon) => {
      if (!mapBounds) return true; // Show all if no map bounds available

      const polygonBounds = getPolygonBounds(polygon.geometry);
      if (!polygonBounds) return true; // Show if can't calculate bounds

      // Hide polygon if map bounds are within polygon bounds
      return !isMapBoundsWithinPolygon(mapBounds, polygonBounds);
    });

    const result = {
      points: [
        ...featuresToReturn.filter((f) => {
          if (!f?.properties) return false;
          if (
            f.properties?.objectType !== "Deviation" && 
            f.properties?.geometryType !== "MultiPolygon" &&
            f.properties?.geometryType !== "Polygon" &&
            !!f.geometry.coordinates[0]
          ) {
            return f;
          } else return false;
        }),
      ],
      polygons: visiblePolygons,
    };
    return result;
  }, [
    displayedItems,
    deviationId,
    inspectionId,
    companyId,
    currentItem,
    mapBounds,
  ]);
}


export const MapFeatures = () => {
  const features = useFeatures();
    const {mapRef} = useAppContext();
    return (
    <>
    <GeoJSONFiles />
      {/* Separate source for polygons - render first so they're behind points */}
      {features.polygons?.length > 0 && (
        <Source
          id={`${mapSettings.uniqueSourcePrefix}-polygons`}
          type="geojson"
          data={{ type: "FeatureCollection", features: features.polygons }}
        >
          {/* Polygon fill layer */}
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-polygon-fill`}
            type="fill"
            paint={{
              "fill-color": "#0080ff", // blue color fill
              "fill-opacity": 0.5,
            }}
          />
          {/* Polygon stroke layer for better visibility */}
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-polygon-stroke`}
            type="line"
            paint={{
              "line-color": "#0066cc",
              "line-width": 2,
              "line-opacity": 0.8,
            }}
          />
        </Source>
      )}

      {features.points?.length > 0 && (
        <Source
          id={mapSettings.uniqueSourcePrefix}
          type="geojson"
          data={{ type: "FeatureCollection", features: features.points }}
          cluster={true}
          clusterMaxZoom={9}
          clusterRadius={50}
          clusterProperties={{
            max: ["max", 0, ["get", "scalerank"]],
            sum: ["+", 0, ["get", "scalerank"]],
            has_island: [
              "any",
              false,
              ["==", ["get", "featureclass"], "island"],
            ],
          }}
        >
           {mapRef?.current?.getSource(mapSettings.uniqueSourcePrefix) && <>
           
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-points`}
            type="circle"
            source={mapSettings.uniqueSourcePrefix}
            filter={["==", ["get", "geometryType"], "Point"]}
            paint={{
              // Use zoom-based sizing so points are smaller when zoomed out
              // and grow gradually as the user zooms in, which reduces
              // visual clutter and overlapping “blobs”.
              "circle-radius": [
                "interpolate",
                ["linear"],
                ["zoom"],
                4,
                3,
                8,
                5,
                12,
                7,
                16,
                9,
              ],
              "circle-color": ["get", "markerColor"],
              // Slightly thicker, high‑contrast outline so markers remain
              // visually distinct even when they are close together.
              "circle-stroke-width": 3,
              "circle-stroke-color": "#ffffff",
              // Make fills a bit more transparent so overlapping points
              // are easier to distinguish.
              "circle-opacity": 0.7,
              "circle-stroke-opacity": 1,
            }}
          />
          
          

          {/* LineString features */}
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-lines`}
            type="line"
            source={mapSettings.uniqueSourcePrefix}
            filter={[
              "in",
              ["get", "geometryType"],
              ["literal", ["LineString", "MultiLineString"]],
            ]}
            paint={{
              "line-color": ["get", "markerColor"],
              "line-width": 3,
              "line-opacity": 0.8,
            }}
          />

          {/* Clusters for points only, and set the color of the count text to White */}
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-clusters`}
            type="circle"
            source={mapSettings.uniqueSourcePrefix}
            filter={["has", "point_count"]}
            paint={{
              "circle-color": [
                "step",
                ["get", "point_count"],
                "#2040B3",
                20,
                "#AA20AA",
                50,
                "#FaAA20",
              ],
              "circle-radius": [
                "step",
                ["get", "point_count"],
                20,
                100,
                30,
                750,
                40,
              ],
            }}
          />

          {/* Cluster count labels */}
          <Layer
            id={`${mapSettings.uniqueSourcePrefix}-cluster-count`}
            type="symbol"
            source={mapSettings.uniqueSourcePrefix}
            filter={["has", "point_count"]}
            layout={{
              "text-field": "{point_count_abbreviated}",
              "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
              "text-size": 12,
            }}
            paint={{ "text-color": "#FFFFFF" }}
          />
          
          </>}
        </Source>
      )}
    </>
  );
};
